import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Rating,
  FormControlLabel,
  Checkbox,
  Chip,
  IconButton,
  Divider,
  Alert
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Star as StarIcon,
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import axios from 'axios';
import Swal from 'sweetalert2';

const Feedback360Form = () => {
  const { reviewId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [review, setReview] = useState(null);
  const [formData, setFormData] = useState({
    reviewerType: 'PEER',
    ratings: [],
    overallComments: '',
    strengths: [''],
    improvements: [''],
    isAnonymous: false
  });

  useEffect(() => {
    fetchReview();
  }, [reviewId]);

  const fetchReview = async () => {
    try {
      const response = await axios.get(`/api/employee-performance/review/${reviewId}`);
      if (response.data.success) {
        const reviewData = response.data.data;
        setReview(reviewData);
        
        // Initialize ratings from KPIs
        const initialRatings = reviewData.kpiEvaluations.map(kpi => ({
          kpiId: kpi.kpiId,
          kpiName: kpi.kpiName,
          score: 3,
          comments: ''
        }));
        setFormData(prev => ({ ...prev, ratings: initialRatings }));
      }
    } catch (error) {
      Swal.fire('Error', 'Failed to fetch review details', 'error');
    }
  };

  const handleRatingChange = (index, field, value) => {
    const newRatings = [...formData.ratings];
    newRatings[index][field] = value;
    setFormData(prev => ({ ...prev, ratings: newRatings }));
  };

  const addStrength = () => {
    setFormData(prev => ({ ...prev, strengths: [...prev.strengths, ''] }));
  };

  const updateStrength = (index, value) => {
    const newStrengths = [...formData.strengths];
    newStrengths[index] = value;
    setFormData(prev => ({ ...prev, strengths: newStrengths }));
  };

  const removeStrength = (index) => {
    setFormData(prev => ({
      ...prev,
      strengths: prev.strengths.filter((_, i) => i !== index)
    }));
  };

  const addImprovement = () => {
    setFormData(prev => ({ ...prev, improvements: [...prev.improvements, ''] }));
  };

  const updateImprovement = (index, value) => {
    const newImprovements = [...formData.improvements];
    newImprovements[index] = value;
    setFormData(prev => ({ ...prev, improvements: newImprovements }));
  };

  const removeImprovement = (index) => {
    setFormData(prev => ({
      ...prev,
      improvements: prev.improvements.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async () => {
    // Validation
    const hasAllRatings = formData.ratings.every(r => r.score > 0);
    if (!hasAllRatings) {
      Swal.fire('Error', 'Please provide ratings for all KPIs', 'error');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        strengths: formData.strengths.filter(s => s.trim() !== ''),
        improvements: formData.improvements.filter(i => i.trim() !== '')
      };

      const response = await axios.post(`/api/employee-performance/feedback360/${reviewId}`, payload);
      
      if (response.data.success) {
        Swal.fire('Success', 'Feedback submitted successfully', 'success');
        navigate('/userdashboard/staff/hrm/performance/reviews');
      }
    } catch (error) {
      Swal.fire('Error', error.response?.data?.message || 'Failed to submit feedback', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (!review) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/userdashboard/staff/hrm/performance/reviews')}>
          <BackIcon />
        </IconButton>
        <Typography variant="h5" fontWeight={600}>
          360° Feedback
        </Typography>
      </Box>

      <Paper sx={{ p: 3 }}>
        {/* Review Info */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Employee:</strong> {review.employeeId?.staff_name} ({review.employeeId?.emp_id})
          </Typography>
          <Typography variant="body2">
            <strong>Review Period:</strong> {review.reviewPeriod} 
            ({new Date(review.reviewStartDate).toLocaleDateString()} - {new Date(review.reviewEndDate).toLocaleDateString()})
          </Typography>
        </Alert>

        <Grid container spacing={3}>
          {/* Reviewer Type */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              select
              label="Your Relationship to Employee"
              value={formData.reviewerType}
              onChange={(e) => setFormData(prev => ({ ...prev, reviewerType: e.target.value }))}
              SelectProps={{ native: true }}
            >
              <option value="SELF">Self Assessment</option>
              <option value="MANAGER">Manager</option>
              <option value="PEER">Peer/Colleague</option>
              <option value="SUBORDINATE">Subordinate</option>
              <option value="CUSTOMER">Customer</option>
            </TextField>
          </Grid>

          {/* Anonymous Option */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isAnonymous}
                  onChange={(e) => setFormData(prev => ({ ...prev, isAnonymous: e.target.checked }))}
                />
              }
              label="Submit feedback anonymously"
            />
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* KPI Ratings */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Rate Performance on Key Indicators
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Rate each KPI from 1 (Poor) to 5 (Excellent)
            </Typography>
          </Grid>

          {formData.ratings.map((rating, index) => (
            <Grid item xs={12} key={index}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  {rating.kpiName}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Rating
                    value={rating.score}
                    onChange={(e, value) => handleRatingChange(index, 'score', value)}
                    size="large"
                    icon={<StarIcon fontSize="inherit" />}
                  />
                  <Chip label={`${rating.score}/5`} color="primary" />
                </Box>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Comments (Optional)"
                  value={rating.comments}
                  onChange={(e) => handleRatingChange(index, 'comments', e.target.value)}
                  placeholder="Provide specific examples or observations"
                />
              </Paper>
            </Grid>
          ))}

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* Strengths */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Strengths</Typography>
              <Button startIcon={<AddIcon />} onClick={addStrength} size="small">
                Add Strength
              </Button>
            </Box>
          </Grid>

          {formData.strengths.map((strength, index) => (
            <Grid item xs={12} key={index}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  fullWidth
                  label={`Strength ${index + 1}`}
                  value={strength}
                  onChange={(e) => updateStrength(index, e.target.value)}
                  placeholder="What does this person do well?"
                />
                {formData.strengths.length > 1 && (
                  <IconButton color="error" onClick={() => removeStrength(index)}>
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>
            </Grid>
          ))}

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* Areas for Improvement */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Areas for Improvement</Typography>
              <Button startIcon={<AddIcon />} onClick={addImprovement} size="small">
                Add Area
              </Button>
            </Box>
          </Grid>

          {formData.improvements.map((improvement, index) => (
            <Grid item xs={12} key={index}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  fullWidth
                  label={`Improvement Area ${index + 1}`}
                  value={improvement}
                  onChange={(e) => updateImprovement(index, e.target.value)}
                  placeholder="What could be improved?"
                />
                {formData.improvements.length > 1 && (
                  <IconButton color="error" onClick={() => removeImprovement(index)}>
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>
            </Grid>
          ))}

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* Overall Comments */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Overall Comments"
              value={formData.overallComments}
              onChange={(e) => setFormData(prev => ({ ...prev, overallComments: e.target.value }))}
              placeholder="Provide any additional feedback or observations"
            />
          </Grid>

          {/* Actions */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/userdashboard/staff/hrm/performance/reviews')}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? 'Submitting...' : 'Submit Feedback'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default Feedback360Form;
